/* eslint-disable indent */
import { DspType } from '@shure/device-ui/shared/dsp/data-access';
import { AutomixMode } from '@shure/shared/angular/data-access/system-api/core';
import {
	getEnabledFlagForEqualizerFiltersFromSysApi,
	isParametricTypeValid,
	mapAnalogInputUseFromSysApi,
	mapChannelGroupFromSysApi,
	mapChannelRoleFromSysApi,
	mapDanteEncryptionStatusFromSysApi,
	mapDirectOutputTapPointFromSysApi,
	mapDurationFromSysApi,
	mapMicOptimizationModeFromSysApi,
	mapMicPolarPatternFromSysApi,
	mapNoiseDetectionSensitivityFromSysApi,
	mapNoiseRemovalLevelFromSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';

import {
	ChannelAutomixMode,
	IntellimixAutomixChannel,
	IntellimixBasicAutomixChannel,
	IntellimixDspTypes,
	IntellimixInputChannel
} from '../../api/models';
import {
	IntellimixAutomixChannelFragment,
	IntellimixBasicAutomixChannelFragment,
	IntellimixInputChannelFragment
} from '../graphql/generated/intellimix.generated';

export function mapAutomixModeFromSysApi(model?: AutomixMode): ChannelAutomixMode {
	switch (model) {
		case AutomixMode.Custom:
			return ChannelAutomixMode.Custom;
		case AutomixMode.GainSharing:
			return ChannelAutomixMode.GainSharing;
		case AutomixMode.Gating:
			return ChannelAutomixMode.Gating;
		case AutomixMode.Manual:
			return ChannelAutomixMode.Manual;
		case AutomixMode.Classic:
			return ChannelAutomixMode.Classic;
		case AutomixMode.Smooth:
			return ChannelAutomixMode.Smooth;
		default:
			return ChannelAutomixMode.Gating;
	}
}

export function mapInputChannelFromSysApi(channelSysApi: IntellimixInputChannelFragment): IntellimixInputChannel {
	const {
		id,
		description: { groupName, role, features, constraints },
		features: {
			danteEncryptionStatus,
			automixerInputAlwaysOn,
			automixerInputPriority,
			automixerInputSendToMonoMix,
			name,
			automaticGainControl,
			postGateGain,
			postGateMute,
			directOutputTapPoint,
			micPolarPattern,
			lobeConfiguration,
			postGateSolo,
			automixerChannelActive
		}
	} = channelSysApi;

	return {
		id,
		role: mapChannelRoleFromSysApi(role),
		group: mapChannelGroupFromSysApi(groupName),
		features: {
			channelName: name ? name.name : '',
			danteEncryptionStatus: {
				status: mapDanteEncryptionStatusFromSysApi(danteEncryptionStatus?.status),
				isMissing: !features.danteEncryptionStatus.supported
			},

			postGateGain: {
				gain: postGateGain?.gain ?? -60,
				isMissing: !features.postGateGain.supported
			},

			postGateMuted: {
				muted: postGateMute?.muted || false,
				isMissing: !features.postGateMute.supported
			},
			alwaysOn: {
				enabled: automixerInputAlwaysOn?.enabled || false,
				isMissing: !features?.automixerInputAlwaysOn?.supported
			},

			priority: {
				enabled: automixerInputPriority?.enabled || false,
				isMissing: !features.automixerInputPriority.supported
			},

			sendToMix: {
				enabled: automixerInputSendToMonoMix?.enabled || false,
				isMissing: !features.automixerInputSendToMonoMix?.supported
			},

			supportAecPostMetering:
				features.acousticEchoCancellationPeak?.supported && features.acousticEchoCancellationRms.supported,
			supportAutomixerInputMetering:
				features.automixerInputPeak.supported && features.automixerInputRms.supported,

			agc: {
				enabled: automaticGainControl?.enabled || false,
				isMissing: !features.automaticGainControl.supported
			},
			directOutputTapPoint: {
				tapPoint: mapDirectOutputTapPointFromSysApi(directOutputTapPoint?.tapPoint),
				isMissing: !features.directOutputTapPoint.supported
			},
			micPolarPattern: {
				pattern: mapMicPolarPatternFromSysApi(micPolarPattern?.pattern),
				isMissing: !features.micPolarPattern.supported
			},
			lobeConfiguration: {
				enabled: lobeConfiguration?.enabled || false,
				isMissing: !features.lobeConfiguration.supported
			},
			postGateSolo: {
				enabled: postGateSolo?.enabled ?? false,
				isMissing: !features.postGateSolo.supported
			},
			automixerChannelActive: {
				enabled: automixerChannelActive?.enabled ?? false,
				isMissing: !features.automixerChannelActive.supported
			}
		},
		constraints: {
			gain: {
				range: {
					min: constraints.gain?.gain.range.min ?? -110,
					max: constraints.gain?.gain.range.max ?? 30,
					step: constraints.gain?.gain.range.step ?? 0.1
				}
			},
			postGateGain: {
				range: {
					min: constraints.postGateGain?.gain.range.min ?? -110,
					max: constraints.postGateGain?.gain.range.max ?? 30,
					step: constraints.postGateGain?.gain.range.step ?? 0.1
				}
			},
			directOutputTapPointOptions: constraints.directOutputTapPoint?.tapPoint?.options.map(
				mapDirectOutputTapPointFromSysApi
			)
		}
	};
}

export function mapBasicAutomixChannelFromSysApi(
	channelSysApi: IntellimixBasicAutomixChannelFragment
): IntellimixBasicAutomixChannel {
	const {
		id,
		description: { features, groupName, role },
		features: { danteEncryptionStatus, name, postGateMute }
	} = channelSysApi;

	return {
		id,
		role: mapChannelRoleFromSysApi(role),
		group: mapChannelGroupFromSysApi(groupName),
		features: {
			channelName: name ? name.name : '',
			danteEncryptionStatus: {
				status: mapDanteEncryptionStatusFromSysApi(danteEncryptionStatus?.status),
				isMissing: !features.danteEncryptionStatus.supported
			},
			postGateMuted: {
				muted: postGateMute?.muted || false,
				isMissing: !features.postGateMute.supported
			}
		}
	};
}

export function mapAutomixChannelFromSysApi(channelSysApi: IntellimixAutomixChannelFragment): IntellimixAutomixChannel {
	const {
		id,
		description: { features, groupName, role, constraints },
		features: {
			danteEncryptionStatus,
			name,
			gain,
			mute,
			automixer,
			equalizer,
			acousticEchoCancellation,
			automaticGainControl,
			noiseReduction,
			delay,
			compressor,
			micOptimization,
			automixSourceSelector,
			micPolarPattern,
			lobeConfiguration,
			postGateMute,
			postGateGain,
			voiceActivityDetectionSensitivity,
			voiceActivityDetectionNoiseReduction,
			analogInputUse,
			automixerChannelActive
		}
	} = channelSysApi;

	return {
		id,
		role: mapChannelRoleFromSysApi(role),
		group: mapChannelGroupFromSysApi(groupName),
		features: {
			channelName: name ? name.name : '',
			availableDsps: getAvailableDsps(channelSysApi),
			danteEncryptionStatus: {
				status: mapDanteEncryptionStatusFromSysApi(danteEncryptionStatus?.status),
				isMissing: !features.danteEncryptionStatus.supported
			},
			gain: {
				gain: gain?.gain || 0,
				isMissing: !features.gain.supported
			},
			postGateGain: {
				gain: postGateGain?.gain ?? 0,
				isMissing: !features.postGateGain.supported
			},
			mute: {
				muted: mute?.muted || false,
				isMissing: !features.mute.supported
			},
			postGateMuted: {
				muted: postGateMute?.muted ?? false,
				isMissing: !features.postGateMute.supported
			},
			peq: {
				ids: equalizer?.filters.map((filter) => filter.id) || [],
				enabled: getEnabledFlagForEqualizerFiltersFromSysApi(equalizer?.filters || null),
				isParametricTypeValid: isParametricTypeValid(equalizer?.filters || null),
				isMissing: !features.equalizer.supported
			},
			aec: {
				enabled: acousticEchoCancellation?.enabled || false,
				isMissing: !features.acousticEchoCancellation.supported
			},
			comp: {
				enabled: compressor?.enabled || false,
				isMissing: !features.compressor.supported
			},
			agc: {
				enabled: automaticGainControl?.enabled || false,
				isMissing: !features.automaticGainControl.supported
			},
			nr: {
				enabled: noiseReduction?.enabled || false,
				isMissing: !features.noiseReduction.supported
			},
			delay: {
				enabled: delay?.enabled || false,
				isMissing: !features.delay.supported
			},
			automixSourceSelector: {
				id: automixSourceSelector?.source?.id ?? '',
				isMissing: !features.automixSourceSelector.supported
			},
			micOptimization: {
				constraints: {
					options: constraints.micOptimization
						? constraints.micOptimization.mode.options.map((option) =>
								mapMicOptimizationModeFromSysApi(option)
						  )
						: []
				},
				mode: mapMicOptimizationModeFromSysApi(micOptimization?.mode),
				isMissing: !features.micOptimization.supported
			},
			noiseDetectionSensitivity: {
				constraints: {
					options: constraints.voiceActivityDetectionSensitivity?.sensitivity
						? constraints.voiceActivityDetectionSensitivity.sensitivity.options.map((option) =>
								mapNoiseDetectionSensitivityFromSysApi(option)
						  )
						: []
				},
				noiseDetectionSensitivity: mapNoiseDetectionSensitivityFromSysApi(
					voiceActivityDetectionSensitivity?.sensitivity
				),
				isMissing: !features.voiceActivityDetectionSensitivity.supported
			},
			noiseRemovalLevel: {
				constraints: {
					options: constraints.voiceActivityDetectionNoiseReduction?.level
						? constraints.voiceActivityDetectionNoiseReduction.level.options.map((option) =>
								mapNoiseRemovalLevelFromSysApi(option)
						  )
						: []
				},
				noiseRemovalLevel: mapNoiseRemovalLevelFromSysApi(voiceActivityDetectionNoiseReduction?.level),
				isMissing: !features.voiceActivityDetectionNoiseReduction.supported
			},
			automixer: {
				enabled: automixer?.enabled ?? false,
				constraints: {
					mode: {
						supportedModes:
							constraints.automixer?.mode.options.map((option) => mapAutomixModeFromSysApi(option)) || []
					},
					gatingSensitivity: {
						range: {
							min: constraints.automixer?.gatingSensitivity.range.min ?? 1,
							max: constraints.automixer?.gatingSensitivity.range.max ?? 9,
							step: constraints.automixer?.gatingSensitivity.range.step ?? 1
						}
					},
					maxOpenChannels: {
						range: {
							min: automixer?.maxOpenChannelsRange.min ?? 1,
							max: automixer?.maxOpenChannelsRange.max ?? 8,
							step: automixer?.maxOpenChannelsRange.step ?? 1
						}
					},
					offAttenuation: {
						range: {
							min: constraints.automixer?.offAttenuation.range.min ?? -110,
							max: constraints.automixer?.offAttenuation.range.max ?? -3,
							step: constraints.automixer?.offAttenuation.range.step ?? 1
						}
					},
					holdTime: {
						range: {
							min: constraints.automixer?.holdTime.range
								? mapDurationFromSysApi(constraints.automixer?.holdTime.range.min)
								: 100,
							max: constraints.automixer?.holdTime.range
								? mapDurationFromSysApi(constraints.automixer?.holdTime.range.max)
								: 1500,
							step: constraints.automixer?.holdTime.range
								? mapDurationFromSysApi(constraints.automixer?.holdTime.range.step)
								: 1
						}
					}
				},
				offAttenuation: {
					value: automixer?.offAttenuation ?? 0,
					isMissing: !features.automixer.supported
				},
				gatingSensitivity: {
					value: automixer?.gatingSensitivity ?? 0,
					isMissing: !features.automixer.supported
				},
				leaveLastChannelOn: {
					enabled: automixer?.leaveLastChannelOn ?? false,
					isMissing: !features.automixer.supported
				},
				maxOpenChannels: {
					value: automixer?.maxOpenChannels ?? 1,
					isMissing: !features.automixer.supported
				},
				holdTime: {
					value: mapDurationFromSysApi(automixer?.holdTime),
					isMissing: !features.automixer.supported
				},
				mode: {
					mode: mapAutomixModeFromSysApi(automixer?.mode),
					isMissing: !features.automixer.supported
				},
				isMissing: !features.automixer.supported
			},
			micPolarPattern: features.micPolarPattern.supported
				? {
						pattern: mapMicPolarPatternFromSysApi(micPolarPattern?.pattern),
						isMissing: !!features.micPolarPattern
				  }
				: undefined,
			lobeConfiguration: {
				enabled: lobeConfiguration?.enabled || false,
				isMissing: !features.lobeConfiguration.supported
			},
			analogInputUse: {
				mode: mapAnalogInputUseFromSysApi(analogInputUse?.mode),
				isMissing: !features.analogInputUse.supported
			},
			automixerChannelActive: {
				enabled: automixerChannelActive?.enabled ?? false,
				isMissing: !features.automixerChannelActive.supported
			}
		},
		constraints: {
			gain: {
				range: {
					min: constraints.gain?.gain.range.min ?? -110,
					max: constraints.gain?.gain.range.max ?? 30,
					step: constraints.gain?.gain.range.step ?? 0.1
				}
			},
			postGateGain: {
				range: {
					min: constraints.gain?.gain.range.min ?? -110,
					max: constraints.gain?.gain.range.max ?? 30,
					step: constraints.gain?.gain.range.step ?? 0.1
				}
			}
		}
	};
}

export function getAvailableDsps(intellimixChannelSysApi: IntellimixAutomixChannelFragment): IntellimixDspTypes[] {
	const { description } = intellimixChannelSysApi;
	const availableDsps: IntellimixDspTypes[] = [];

	if (description.features.automaticGainControl.supported) {
		availableDsps.push(DspType.Agc);
	}
	if (description.features.acousticEchoCancellation.supported) {
		availableDsps.push(DspType.Aec);
	}
	if (description.features.noiseReduction.supported) {
		availableDsps.push(DspType.Nr);
	}
	if (description.features.equalizer.supported) {
		availableDsps.push(DspType.Peq);
	}
	if (description.features.compressor.supported) {
		availableDsps.push(DspType.Compressor);
	}
	if (description.features.delay.supported) {
		availableDsps.push(DspType.Delay);
	}

	return availableDsps;
}
